// 친구랑 같이하기 — the gameplay protocol over a gamzone/net/peer.ts Session.
// Othello is turn-based and fully deterministic (no hidden info), so unlike
// the reference's real-time versus (simultaneous boards kept in sync by a
// shared RNG seed), each move is just forwarded and applied directly — no
// seed-replay needed. Only these small JSON messages cross the wire:
//   {type:"ready"}                       guest → host: wired up, ready for `start`
//   {type:"start", bgIdx, portraitIdx}   host → guest: begin a match with this
//                                        random cosmetic pick (host is always
//                                        dark/first)
//   {type:"move",  cell}                 either → other: a disc placed at `cell`
//   {type:"rematch-offer"}               loser → winner: "another round?"
//   {type:"rematch-accept"}              winner → loser: "sure"
//   {type:"rematch", bgIdx, portraitIdx} either → other: re-roll cosmetics, go again
//
// Transport-only: it (de)serializes and forwards to the callbacks the caller
// supplies; it holds no game state. Mirrors the reference's net/versus.js API.

import type { Session } from "../../gamzone/net/peer.ts";
import { randomCosmetic, type FriendCosmetic } from "./cosmetics.ts";

export interface FriendNetCallbacks {
  onReady?: () => void;
  onStart?: (cosmetic: FriendCosmetic) => void;
  onMove?: (cell: number) => void;
  onRematchOffer?: () => void;
  onRematchAccept?: () => void;
  onRematch?: (cosmetic: FriendCosmetic) => void;
  onPeerLost?: () => void;
  onPeerClosed?: () => void;
}

export function createFriendNet(
  session: Session,
  { on = {} }: { on?: FriendNetCallbacks } = {},
) {
  const detach = session.onMessage((raw) => {
    const msg = raw as Record<string, unknown> | null;
    if (!msg || typeof msg !== "object") return;
    switch (msg.type) {
      case "ready":
        on.onReady?.();
        break;
      case "start":
        on.onStart?.({
          bgIdx: (msg.bgIdx as number) | 0,
          portraitIdx: (msg.portraitIdx as number) | 0,
        });
        break;
      case "move":
        on.onMove?.((msg.cell as number) | 0);
        break;
      case "rematch-offer":
        on.onRematchOffer?.();
        break;
      case "rematch-accept":
        on.onRematchAccept?.();
        break;
      case "rematch":
        on.onRematch?.({
          bgIdx: (msg.bgIdx as number) | 0,
          portraitIdx: (msg.portraitIdx as number) | 0,
        });
        break;
      default:
        break;
    }
  });
  const detachLost = session.onLost?.(() => on.onPeerLost?.());
  const detachClose = session.onClose?.(() => on.onPeerClosed?.());

  return {
    /** Guest: tell the host we've wired up and are ready for the start pick. */
    sendReady() {
      session.send({ type: "ready" });
    },
    /** Host: roll + broadcast a cosmetic pick. Returns it so the host applies it too. */
    startMatch(): FriendCosmetic {
      const cosmetic = randomCosmetic();
      session.send({ type: "start", ...cosmetic });
      return cosmetic;
    },
    sendMove(cell: number) {
      session.send({ type: "move", cell: cell | 0 });
    },
    /** Loser: ask for a rematch (the winner's accept button enables on receipt). */
    sendRematchOffer() {
      session.send({ type: "rematch-offer" });
    },
    /** Winner: accept the loser's rematch request. */
    sendRematchAccept() {
      session.send({ type: "rematch-accept" });
    },
    /** Loser (either peer, once accepted): re-roll cosmetics and start again. */
    rematch(): FriendCosmetic {
      const cosmetic = randomCosmetic();
      session.send({ type: "rematch", ...cosmetic });
      return cosmetic;
    },
    dispose() {
      detach?.();
      detachLost?.();
      detachClose?.();
    },
  };
}
